import React, { useCallback, useState } from 'react';
import HarbourButton from '../../components/harbour/HarbourButton';
import HarbourModal from '../../components/harbour/HarbourModal';
import HarbourNotice from '../../components/harbour/HarbourNotice';
import HarbourIcon from '../../components/harbour/HarbourIcon';
import HarbourToast from '../../components/harbour/HarbourToast';
import FamilyConfirmSheet from './FamilyConfirmSheet';
import { apiFamilyPathwayConsent } from '../../utils/rmsApi';
import { familyConsentVersion } from '../../constants/consentVersions';
import { getServiceContact } from '../../constants/pathwaySettings';
import './familyWithdraw.css';

/*
 * Family portal · taking back permission (TDI-473 referral, TDI-479 SEND).
 *
 * Built verbatim to the consent withdrawal frames (Figma 5326:2): the
 * tertiary entry "Take back your permission", the choose-how step, the
 * confirm with the four lines Tom asked for on 24 October (including that
 * safeguarding carries on), the alternative-contact step, and the toast.
 * Phone: bottom sheets; laptop: centred dialogs. The server
 * (rmsFamilyPathwayConsent, 'withdraw') does the stopping.
 *
 * pathway:
 *   'referral'  the alternative is the BTH service (its phone and email);
 *               a refusal because the outcome letter has gone turns the
 *               confirm into the contact step, as the frames draw it (P-5).
 *   'sen'       the alternative is the school (the SENCO's name and phone,
 *               E-3); there is no "too late", the family can leave at any
 *               stage and keeps what is done.
 *
 * mode:
 *   'entry'  the pathway can still be withdrawn here;
 *   'notice' (referral only) the outcome letter has gone, so the family is
 *            pointed at the service instead (P-10);
 *   anything else renders nothing.
 */

// Everything before the outcome letter: not started, forms in progress on
// either side, sent and waiting, and in review.
const WITHDRAWABLE_STATES = new Set([
  'notStarted', 'inProgress', 'waitingForSchool', 'sentWaitingForReview', 'inReview',
]);
const PAST_WITHDRAWAL_STATES = new Set(['outcomeAvailable', 'closed']);

/** Which of the two surfaces a referral should show, from its pathway states. */
export function withdrawModeFor({ stopped, consent, pathwayStates }) {
  const states = (pathwayStates || []).filter(Boolean);
  if (stopped || consent !== 'confirmed' || states.length === 0) return null;
  if (states.some((s) => PAST_WITHDRAWAL_STATES.has(s))) return 'notice';
  if (states.every((s) => WITHDRAWABLE_STATES.has(s))) return 'entry';
  return null;
}

/**
 * Whether the SEND pathway offers the entry: a live pathway the family has
 * agreed to, at any stage (E-1 draws it at Assess; after the profile the
 * family can still leave and keeps the profile, TDI-479).
 */
export function senWithdrawModeFor({ stopped, consent }) {
  if (stopped || consent !== 'confirmed') return null;
  return 'entry';
}

/** "Tell them you want to take back your permission:" plus one block per service. */
export function ServiceContactBlocks({ orgId, pathways, lead }) {
  const contacts = (pathways || []).map((p) => getServiceContact(orgId, p)).filter(Boolean);
  if (contacts.length === 0) return null;
  return (
    <div className="family-withdraw__contacts">
      {lead && <p className="harbour-body-16">{lead}</p>}
      {contacts.map((c) => (
        <div key={c.name} className="family-withdraw__service">
          <p className="harbour-body-16-bold">{c.name}</p>
          <a className="family-withdraw__link" href={`mailto:${c.email}`}>
            <HarbourIcon name="email" size={20} />
            {c.email}
          </a>
          <a className="family-withdraw__link" href={c.phoneHref}>
            <HarbourIcon name="phone" size={20} />
            {c.phoneLabel}
          </a>
        </div>
      ))}
    </div>
  );
}

/**
 * "Contact Priya Shah, the SENCO at Test Primary School." and the school
 * phone (E-3). Each missing part drops out rather than leaving a hole.
 */
export function schoolContactLine({ sencoName, schoolName } = {}) {
  const who = sencoName ? `${sencoName}, the SENCO` : 'the SENCO';
  return `Contact ${schoolName ? `${who} at ${schoolName}` : who}.`;
}

function SchoolContactBlock({ schoolContact }) {
  const phone = (schoolContact && schoolContact.phone) || '';
  return (
    <div className="family-withdraw__contacts">
      <p className="harbour-body-16">{schoolContactLine(schoolContact)}</p>
      {phone && (
        <a className="family-withdraw__link" href={`tel:${phone.replace(/\s+/g, '')}`}>
          <HarbourIcon name="phone" size={20} />
          {phone}
        </a>
      )}
    </div>
  );
}

function ChooseHow({ choice, onChoice, pathway, childName }) {
  const sen = pathway === 'sen';
  return (
    <fieldset className="family-withdraw__choices">
      <legend className="family-withdraw__legend">How do you want to do this?</legend>
      <label className={`family-withdraw__option${choice === 'here' ? ' family-withdraw__option--selected' : ''}`}>
        <input type="radio" name="withdraw-how" value="here" checked={choice === 'here'} onChange={() => onChoice('here')} />
        <span>
          <span className="harbour-body-16-bold">Take it back here</span>
          <span className="harbour-body-16">
            {sen ? 'The SEND pathway stops now. Your answers are kept.' : 'The referral stops now. Your answers are kept.'}
          </span>
        </span>
      </label>
      <label className={`family-withdraw__option${choice === 'service' ? ' family-withdraw__option--selected' : ''}`}>
        <input type="radio" name="withdraw-how" value="service" checked={choice === 'service'} onChange={() => onChoice('service')} />
        <span>
          <span className="harbour-body-16-bold">{sen ? 'Tell the school instead' : 'Contact the service instead'}</span>
          <span className="harbour-body-16">
            {sen ? `See how to contact ${childName}’s school.` : 'Get the service’s phone number and email.'}
          </span>
        </span>
      </label>
    </fieldset>
  );
}

export default function FamilyWithdrawConsent({
  mode,
  pathway = 'referral',
  orgId,
  patientRecordId,
  childName,
  concernWords,
  pathways,
  schoolContact,
  isDesktop,
  onWithdrawn,
}) {
  const [step, setStep] = useState(null); // null | 'choose' | 'confirm' | 'contact'
  const [choice, setChoice] = useState('here');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [toast, setToast] = useState(false);
  const sen = pathway === 'sen';

  const close = useCallback(() => {
    if (busy) return;
    setStep(null);
    setFailed(false);
  }, [busy]);

  const open = () => {
    setChoice('here');
    setFailed(false);
    setStep('choose');
  };

  const chooseContinue = () => setStep(choice === 'service' ? 'contact' : 'confirm');

  const withdraw = async () => {
    setBusy(true);
    setFailed(false);
    try {
      await apiFamilyPathwayConsent({
        orgId, patientRecordId, pathway, decision: 'withdraw',
        consentVersion: familyConsentVersion(pathway),
      });
      setStep(null);
      setToast(true);
      if (typeof onWithdrawn === 'function') onWithdrawn();
    } catch (e) {
      // Past the outcome letter there is nothing left to stop: the server
      // says so and points at the service, so the screen does the same.
      if (!sen && e?.details?.contactTheService) {
        setStep('contact');
      } else {
        setFailed(true);
      }
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'notice') {
    return (
      <div className="family-withdraw__notice">
        <HarbourNotice kind="info" title="To take back your permission now, contact the service:" />
        <ServiceContactBlocks orgId={orgId} pathways={pathways} />
      </div>
    );
  }
  if (mode !== 'entry') return null;

  const Dialog = isDesktop ? HarbourModal : FamilyConfirmSheet;
  const confirmTitle = sen
    ? `Take back your permission for ${childName}’s SEND pathway?`
    : `Take back your permission for ${childName}’s ${concernWords ? `${concernWords} ` : ''}referral?`;

  return (
    <>
      <HarbourButton kind="tertiary" className="family-withdraw__entry" onClick={open}>
        Take back your permission
      </HarbourButton>

      {step === 'choose' && (
        <Dialog
          title="How do you want to do this?"
          confirmLabel="Continue"
          confirmKind="family"
          onConfirm={chooseContinue}
          onClose={close}
        >
          <ChooseHow choice={choice} onChoice={setChoice} pathway={pathway} childName={childName} />
        </Dialog>
      )}

      {step === 'confirm' && (
        <Dialog
          title={confirmTitle}
          confirmLabel="Yes, take it back"
          confirmKind="family"
          busy={busy}
          onConfirm={withdraw}
          onClose={close}
        >
          {failed && <HarbourNotice kind="error" title="We couldn’t do this. Try again." />}
          {sen ? (
            <>
              <p className="harbour-body-16">Nothing more will happen on the SEND pathway. Your answers are kept, and you can still read them.</p>
              <p className="harbour-body-16">If the school has already started a safeguarding process, that will carry on.</p>
              <p className="harbour-body-16">Leaving won&rsquo;t stop the school supporting {childName}.</p>
              <p className="harbour-body-16">You can give your permission again later.</p>
            </>
          ) : (
            <>
              <p className="harbour-body-16">Nothing more will happen on the referral. Your answers are kept, and you can still read them.</p>
              <p className="harbour-body-16">If the service has already started its safeguarding process, that will carry on.</p>
              <p className="harbour-body-16">Changing your mind won&rsquo;t affect the support {childName} gets at school.</p>
              <p className="harbour-body-16">You can give your permission again later.</p>
            </>
          )}
        </Dialog>
      )}

      {step === 'contact' && (
        <Dialog
          title={sen ? 'Tell the school instead' : 'Contact the service'}
          confirmLabel="Close"
          confirmKind="secondary"
          showCancel={false}
          onConfirm={close}
          onClose={close}
        >
          {sen ? (
            <SchoolContactBlock schoolContact={schoolContact} />
          ) : (
            <ServiceContactBlocks
              orgId={orgId}
              pathways={pathways}
              lead="Tell them you want to take back your permission:"
            />
          )}
        </Dialog>
      )}

      {/* The design's toast also says "We've emailed you and the school";
          that line waits until the withdrawal emails exist (TDI-477). */}
      {toast && <HarbourToast title="Permission taken back" onClose={() => setToast(false)} />}
    </>
  );
}
